import { api } from "./api.ts";
import { t } from "./i18n.ts";
import { createStore } from "./store.ts";
import { EPK_FONT_GROUPS } from "../../shared/constants.ts";
import { customFontValue, googleFontWeights, parseFontValue, type CustomFont } from "../../shared/fonts.ts";

/**
 * The font picker's DATA - the one source every surface picks type from.
 *
 * Every surface this app makes - the app's own chrome, the press kit, the smart link, the reels
 * - picks type from here, so a font means the same thing everywhere and an upload appears in all
 * four the moment it lands. Two kinds of entry sit in the same dropdown: the Google families the
 * app has always offered, and the owner's own uploads (Settings → Appearance → Fonts).
 *
 * The reels are the one place that cannot take a Google font: a video is drawn by ffmpeg from a
 * real font FILE, and a Google family is a stylesheet, not a file. Rather than let a picker offer
 * type it cannot draw, `fileBackedOnly` shows the uploads (plus the bundled default) and says why
 * the rest aren't there.
 *
 * The RENDERING lives in `components/fontSelect.ts` (`<gh-font-select>`, round 100): this module
 * is data only. It used to string-build `<option>` markup (`fontOptions`/`fillFontSelect`) so a
 * legacy page could `innerHTML` a native `<select>`; the last three escape-hatch consumers
 * (`artists`/`settings`/`releases`) adopted the component and that string path died with them -
 * the last B.3f shared-module-tail item. `fontOptionGroups` below is the data every picker maps.
 */

/** Every uploaded font, cached for the session - a picker re-renders on every keystroke */
let cache: CustomFont[] | null = null;
let inflight: Promise<CustomFont[]> | null = null;

/**
 * Bumps every time the uploaded-font cache is (re)loaded, so a `<gh-font-select>` can subscribe
 * and re-render when an upload, delete or spacing-save changes the fonts under it - the same
 * live-reaction seam `localeStore` gives a language flip, so an upload in Settings reaches every
 * open picker with no page re-wiring it by hand. The number is opaque: subscribers care only
 * THAT it changed, never to what.
 */
export const fontsStore = createStore(0);

export async function loadCustomFonts(force = false): Promise<CustomFont[]> {
	if (force) {
		cache = null;
		inflight = null;
	}
	if (cache) return cache;
	if (!inflight) {
		inflight = api<{ fonts: CustomFont[] }>("/api/fonts")
			.then(result => {
				cache = result.fonts;
				// Only ever reached on a real (re)load - a cache hit returns above without
				// re-running this - so every bump is a genuine change every picker should show
				fontsStore.update(n => n + 1);
				return cache;
			})
			.catch(() => {
				inflight = null;
				return [];
			});
	}
	return inflight;
}

/** Whatever was loaded last - for synchronous renders that already awaited `loadCustomFonts` */
export function customFonts(): CustomFont[] {
	return cache ?? [];
}

/** The uploaded font a stored value points at, if any */
export function customFontFor(value: string | null | undefined): CustomFont | null {
	const choice = parseFontValue(value);
	if (choice?.kind !== "custom") return null;
	return customFonts().find(font => font.id === choice.id) ?? null;
}

/**
 * The uploads, grouped into families.
 *
 * A font FILE is one weight - "Inter" and "Inter Bold" are two uploads that both call themselves
 * Inter - so a picker that listed the files would show the same name twice and ask the owner to
 * know which is which. It lists FAMILIES; the weight is a separate choice, offered only where it
 * means something (the reels, which draw one weight from one file).
 */
export function fontFamilies(): { family: string; faces: CustomFont[] }[] {
	const families = new Map<string, CustomFont[]>();
	for (const font of customFonts()) {
		const faces = families.get(font.family) ?? [];
		faces.push(font);
		families.set(font.family, faces);
	}
	return [...families.entries()]
		.map(([family, faces]) => ({ family, faces: [...faces].sort((a, b) => a.weight - b.weight) }))
		.sort((a, b) => a.family.localeCompare(b.family));
}

/** The face that speaks for a family in a picker: its regular, else the nearest thing to one */
function canonicalFace(faces: CustomFont[]): CustomFont {
	return faces.reduce((best, face) => (Math.abs(face.weight - 400) < Math.abs(best.weight - 400) ? face : best), faces[0]);
}

/** The weights a chosen font can be DRAWN in - an upload's family, or the Google family's list */
export function weightsFor(value: string | null | undefined): number[] {
	const choice = parseFontValue(value);
	if (!choice) return [];
	if (choice.kind === "google") return googleFontWeights(choice.family);
	const font = customFonts().find(entry => entry.id === choice.id);
	if (!font) return [];
	return [
		...new Set(
			customFonts()
				.filter(entry => entry.family === font.family)
				.map(entry => entry.weight)
		)
	].sort((a, b) => a - b);
}

/**
 * The CSS family a stored font value renders as - the other half of a picker row that IS its face.
 *
 * An upload's family is cosmetic and derived from its id (the `@font-face` binds it), which is the
 * same trick `theme.ts` plays for the app font, so the app never has to ask the server what a file
 * is called. A Google family is its own name, quoted because several of them have spaces in.
 */
export function fontPreviewFamily(value: string | null | undefined): string | undefined {
	const choice = parseFontValue(value);
	if (choice == null) return undefined;
	return choice.kind === "custom" ? `preview-font-${choice.id}` : `"${choice.family}"`;
}

/** Families already asked for this session - a picker re-renders on every keystroke */
const previewedGoogle = new Set<string>();
const previewedCustom = new Set<number>();
const PREVIEW_LINK_ID = "gh-font-preview-link";
const PREVIEW_FACE_ID = "gh-font-preview-faces";

/**
 * Make sure the faces a picker is about to draw its own rows in are actually LOADED.
 *
 * ⚠ **Without this the feature is a lie that looks like a success**: every row renders in the app
 * font, which reads as fifty typefaces that happen to look identical rather than as a stylesheet
 * that never arrived. There is no event to wait for and nothing fails - the browser simply falls
 * back - so this is the half a font-preview picker forgets.
 *
 * Two costs are managed here rather than by the caller:
 *
 * - ⚠ **It runs when a list is OPENED, never when a page renders.** Fifty-one families is fifty-one
 *   font files, and a page that carries a font picker it never opens (every release editor) must
 *   not pay for them.
 * - ⚠ **The Google request is SUBSETTED to the characters in the labels** (`text=`), because the
 *   only text these faces ever draw is the family's own name. That turns each file from a full
 *   latin subset into a few hundred bytes, and it is the difference between a picker that opens and
 *   one that downloads a megabyte to say "Inter" in Inter.
 *
 * The uploads load from our own API and are free of all of that; a browser with no network gets
 * today's behavior back (every row in the app font), which is why this needs no error path.
 */
export function ensureFontPreviews(values: (string | null | undefined)[]): void {
	if (typeof document === "undefined") return;
	const families: string[] = [];
	const ids: number[] = [];
	for (const value of values) {
		const choice = parseFontValue(value);
		if (choice == null) continue;
		if (choice.kind === "google") {
			if (!previewedGoogle.has(choice.family)) families.push(choice.family);
		} else if (!previewedCustom.has(choice.id)) ids.push(choice.id);
	}

	if (ids.length) {
		for (const id of ids) previewedCustom.add(id);
		const style = (document.getElementById(PREVIEW_FACE_ID) as HTMLStyleElement | null) ?? document.createElement("style");
		if (style.id !== PREVIEW_FACE_ID) {
			style.id = PREVIEW_FACE_ID;
			document.head.appendChild(style);
		}
		// A separate family name from `theme.ts`'s `custom-font-<id>` on purpose: that one belongs to
		// the app-wide font and can be swapped or removed out from under a picker that is still open.
		style.textContent += ids
			.map(id => `@font-face { font-family: "preview-font-${id}"; src: url("/api/fonts/${id}/file"); font-display: swap; }`)
			.join("\n");
	}

	if (!families.length) return;
	for (const family of families) previewedGoogle.add(family);
	const all = [...previewedGoogle];
	// The characters the labels actually contain, deduped and sorted so the URL is stable (and
	// therefore cacheable) whatever order the picker asked in.
	const text = [...new Set(all.join("").split(""))].sort().join("");
	const href = `https://fonts.googleapis.com/css2?${all.map(family => `family=${family.replace(/ /g, "+")}`).join("&")}&text=${encodeURIComponent(text)}&display=swap`;
	let link = document.getElementById(PREVIEW_LINK_ID) as HTMLLinkElement | null;
	if (link == null) {
		link = document.createElement("link");
		link.id = PREVIEW_LINK_ID;
		link.rel = "stylesheet";
		document.head.appendChild(link);
	}
	if (link.href !== href) link.href = href;
}

export interface FontOptionsOpts {
	/** The stored font value: a Google family, `custom:<id>`, or null/"" for the default */
	selected: string | null | undefined;
	/** Label for the empty option - what "no choice" means on this surface */
	defaultLabel: string;
	/**
	 * Only offer fonts that exist as a FILE (the reels). Google families are dropped rather than
	 * shown-and-refused, because a picker whose options don't work is worse than a shorter one.
	 */
	fileBackedOnly?: boolean;
}

/** One group of the font list, data-shaped so a Lit island can render real options from it */
export interface FontOptionGroup {
	/** null = the ungrouped default option; a string = an <optgroup> label */
	label: string | null;
	/** The uploads group - the one whose LABEL is app chrome (an island renders it through t()) */
	yours?: true;
	options: { value: string; label: string }[];
}

/**
 * The font list as DATA - the one source every picker renders from. `<gh-font-select>` maps it
 * to real `<option>`/`<optgroup>` templates with `?selected`, because a component may not take
 * an HTML string (no `unsafeHTML`, STYLEGUIDE rule 6).
 */
export function fontOptionGroups(opts: FontOptionsOpts): FontOptionGroup[] {
	const selected = opts.selected || "";
	const groups: FontOptionGroup[] = [{ label: null, options: [{ value: "", label: opts.defaultLabel }] }];
	const families = fontFamilies();
	if (families.length) {
		const chosen = parseFontValue(selected);
		groups.push({
			// Structural only: `yours` tells the consumer to substitute its own t() key
			// (`fontSelect.yoursGroup`), so this string has never reached a screen - it stays because
			// `label` is the group's shape. There used to be a SECOND consumer with a second key, in the
			// Shorts studio; it renders `<gh-font-select>` like everything else now.
			// i18n-ignore: never rendered, see above
			label: "Your fonts",
			yours: true,
			options: families.map(({ family, faces }) => {
				// The family speaks with one voice in the list. If the stored value is one of its
				// faces, that id IS the option's value - so the select's value keeps matching what
				// is stored, whichever weight of the family was picked.
				const stored = chosen?.kind === "custom" && faces.some(face => face.id === chosen.id) ? chosen.id : canonicalFace(faces).id;
				// A family with several uploaded weights says so, in the reader's language: this option label
				// was the last English string in the picker (`" · 3 weights"`), invisible to the audit because a
				// catalog's label is built in plain code, never in a rendered template.
				const weights = faces.length > 1 ? ` · ${t("fontSelect.weightCount", { count: faces.length })}` : "";
				return { value: customFontValue(stored), label: `${family}${weights}` };
			})
		});
	}
	if (!opts.fileBackedOnly) {
		groups.push(...EPK_FONT_GROUPS.map(group => ({ label: group.label, options: group.fonts.map(font => ({ value: font, label: font })) })));
	}
	return groups;
}
